// src/pages/CommentsPage.js
import React, { useState, useEffect } from 'react';
import { useParams} from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase-config';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import axios from 'axios';
import apiClient from '../utils/api';
import './CommentsPage.css'; // We will create this CSS file

const CommentsPage = ({ postId, onClose }) => {
    // const { postId } = useParams();
    // const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editedText, setEditedText] = useState('');

    const [replyingTo, setReplyingTo] = useState(null); 
    const [replyText, setReplyText] = useState('');

    const fetchPost = async () => {
        const postRef = doc(db, 'userNews', postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
            setPost({ id: postSnap.id, ...postSnap.data() });
        } else {
            console.error("No such post!");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPost();
    }, [postId]);

    const handlePostComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !currentUser) return;
        const postRef = doc(db, "userNews", postId);
        const currentComments = post.comments || [];
        const newCommentObj = {
            id: `${currentUser.uid}_${Date.now()}`, userId: currentUser.uid,
            userName: currentUser.displayName || currentUser.email, text: newComment,
            timestamp: new Date(), likes: [], replies: []
        };
        await updateDoc(postRef, { comments: [...currentComments, newCommentObj] });
        setNewComment('');
        fetchPost();
    };

    // --- LIKING A COMMENT ---
    const handleLikeComment = async (commentId) => {
        if (!currentUser) return;
        const postRef = doc(db, "userNews", postId);
        const updatedComments = post.comments.map(comment => {
            if (comment.id === commentId) {
                const likedBy = comment.likes || [];
                if (likedBy.includes(currentUser.uid)) {
                    // Unlike
                    return { ...comment, likes: likedBy.filter(uid => uid !== currentUser.uid) };
                } else {
                    // Like
                    return { ...comment, likes: [...likedBy, currentUser.uid] };
                }
            }
            return comment;
        });
        await updateDoc(postRef, { comments: updatedComments });
        setPost(prev => ({ ...prev, comments: updatedComments })); // Optimistic update
    };

    // --- REPLYING TO A COMMENT ---
    const handlePostReply = async (commentId) => {
        if (!replyText.trim() || !currentUser) return;
        const postRef = doc(db, "userNews", postId);
        const newReplyObj = {
            id: `${currentUser.uid}_${Date.now()}`, userId: currentUser.uid,
            userName: currentUser.displayName || currentUser.email, text: replyText,
            timestamp: new Date()
        };
        const updatedComments = post.comments.map(comment => {
            if (comment.id === commentId) {
                const replies = comment.replies || [];
                return { ...comment, replies: [...replies, newReplyObj] };
            }
            return comment;
        });
        await updateDoc(postRef, { comments: updatedComments });
        setReplyText('');
        setReplyingTo(null);
        fetchPost();
    };

    const handleTranslate = async (commentId, textToTranslate) => {
        const isTelugu = /[\u0C00-\u0C7F]/.test(textToTranslate);
        const targetLang = isTelugu ? 'en' : 'te';
        const sourceLang = isTelugu ? 'te' : 'en';
        try {
            const response = await axios.get(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(textToTranslate)}&langpair=${sourceLang}|${targetLang}`);
            const translatedText = response.data.responseData.translatedText;
            
            // This is the complex state update
            setPost(prev => ({
                ...prev,
                comments: prev.comments.map(c => 
                    c.id === commentId ? { ...c, translatedText: translatedText } : c
                )
            }));
        } catch (error) { console.error("Translation failed:", error); }
    };

    // --- NEW FUNCTION TO DELETE A COMMENT ---
    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) return;

        const postRef = doc(db, "userNews", postId);
        // Filter out the comment to be deleted
        const updatedComments = post.comments.filter(comment => comment.id !== commentId);

        await updateDoc(postRef, { comments: updatedComments });
        fetchPost(); // Refresh the post
    };

    // --- NEW FUNCTIONS TO HANDLE EDITING ---
    const startEditing = (comment) => {
        setEditingCommentId(comment.id);
        setEditedText(comment.text);
    };

    const cancelEditing = () => {
        setEditingCommentId(null);
        setEditedText('');
    };

    const handleUpdateComment = async (e) => {
        e.preventDefault();
        if (!editedText.trim()) return;

        const postRef = doc(db, "userNews", postId);
        const updatedComments = post.comments.map(comment => {
            if (comment.id === editingCommentId) {
                return { ...comment, text: editedText }; // Update the text
            }
            return comment;
        });

        await updateDoc(postRef, { comments: updatedComments });
        cancelEditing();
        fetchPost();
    };
    
    // You would add functions here for handleLikeComment, handleReplyToComment, etc.
    // These functions would find the specific comment in the post state and update it in Firestore.

    if (loading) return <div className="comments-overlay-loader">Loading...</div>;
    if (!post) return <div className="page-container"><p>Post not found.</p></div>;

    return (
        <div className="comments-modal-container">
            <div className="comments-modal-overlay" onClick={onClose}></div>
            <div className="comments-modal-content">
                <header className="comments-header">
                    <button onClick={onClose} className="close-comments-button">✕</button>
                    <h1>Comments</h1>
                    <div style={{ width: '30px' }}></div>
                </header>

            <div className="original-post-summary">
                {/* Optional: Show a small summary of the original post */}
                <h4>{post.title}</h4>
            </div>

            <div className="comments-list-container">
                {post.comments.slice(0).reverse().map(comment => (
                    <div key={comment.id} className="comment-thread">
                        <div className="comment">
                            <div className="comment-header">
                            <p className="comment-author">{comment.userName}</p>
                            {/* Show translated text if it exists, otherwise show original */}
                            {/* --- RENDER EDIT/DELETE BUTTONS CONDITIONALLY --- */}
                                {currentUser && currentUser.uid === comment.userId && (
                                    <div className="comment-owner-actions">
                                        <button onClick={() => startEditing(comment)}><i className="fas fa-pencil-alt"></i></button>
                                        <button onClick={() => handleDeleteComment(comment.id)}><i className="fas fa-trash-alt"></i></button>
                                    </div>
                                )}
                            </div>
                            
                            {/* --- CONDITIONAL EDITING VIEW --- */}
                            {editingCommentId === comment.id ? (
                                <form onSubmit={handleUpdateComment} className="edit-comment-form">
                                    <textarea 
                                        value={editedText} 
                                        onChange={(e) => setEditedText(e.target.value)}
                                        required
                                    />
                                    <div className="edit-form-actions">
                                        <button type="button" onClick={cancelEditing}>Cancel</button>
                                        <button type="submit">Save</button>
                                    </div>
                                </form>
                            ) : (
                                <p className="comment-text">{comment.translatedText || comment.text}</p>
                            )}
                            <div className="comment-actions">
                                <button onClick={() => handleLikeComment(comment.id)} className={comment.likes?.includes(currentUser?.uid) ? 'liked' : ''}>
                                    <i className="fas fa-thumbs-up"></i> Like ({comment.likes?.length || 0})
                                </button>
                                <button onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}>
                                    <i className="fas fa-reply"></i> Reply
                                </button>
                                <button onClick={() => handleTranslate(comment.id, comment.text)}>
                                    <i className="fas fa-language"></i> Translate
                                </button>
                            </div>
                        </div>
                        
                        {/* --- RENDER REPLIES --- */}
                        {(comment.replies || []).length > 0 && (
                            <div className="replies-container">
                                {comment.replies.map(reply => (
                                    <div key={reply.id} className="comment reply">
                                        <p className="comment-author">{reply.userName}</p>
                                        <p className="comment-text">{reply.text}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* --- RENDER REPLY INPUT BOX --- */}
                        {replyingTo === comment.id && (
                            <div className="reply-input-area">
                                <textarea 
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder={`Replying to ${comment.userName}...`}
                                    rows="2"
                                />
                                <button onClick={() => handlePostReply(comment.id)}>Post Reply</button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            <form onSubmit={handlePostComment} className="comment-input-area">
                <textarea 
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a public comment..."
                    required
                ></textarea>
                <button type="submit">Post Comment</button>
            </form>
        </div>
        </div>
    );
};

export default CommentsPage;